/**
 * jr-cancel-flow.js — the Jeremy's Razors cancellation flow.
 *
 * A body-appended overlay, so ONE component serves both hosts: the account page
 * (templates/page.account.liquid) and the account drawer
 * (snippets/jr-account-drawer.liquid). It is opened by jr-account.js, which
 * hands it the subscription and its own HTTP path — so there is one call site,
 * one error contract, and no second copy of the {ok:false} handling.
 *
 * ⚠️ THE OVERLAY CARRIES `jr-account` AS A CLASS. The design tokens
 * (--jr-ink, --jr-ember, …) are declared on `.jr-account` in jr-account.css, so
 * an element appended to <body> sits outside them and every var() resolves to
 * nothing. `.jr-photo` already does this for the same reason; copy the pattern,
 * don't rediscover it.
 *
 * ⚠️ IT HANGS OFF <body>, NOT THE MOUNT. Inside the drawer a fixed element
 * positions against the drawer's transform and is clipped by its
 * `overflow: clip`. z-index clears the drawer's 30 and the theme's tooltip
 * (10001); it sits one above the photo picker's 10002.
 *
 * WHY IT GUESSES BEFORE IT ASKS
 * A reason menu is the least reliable instrument in a cancel flow —
 * self-reported, at the worst moment, from a list we wrote. So step two is a
 * hypothesis computed from the customer's OWN behaviour (skips, a failed
 * charge, an unresolved ticket, a reorder cadence), answered in one tap. When
 * nothing fires it falls through to the menu; a refuted guess is recorded,
 * because being wrong about a customer is worth knowing.
 *
 * THE EXIT IS VISIBLE ON EVERY STEP AND NEVER DISABLED. California's
 * automatic-renewal law requires cancelling to be at least as easy as signing
 * up, and we already have customers who asked to cancel, were not cancelled,
 * and started talking about chargebacks. Deflection comes from persuasion.
 * Friction from hiding the door is what turns into a dispute.
 */
window.JRCancelFlow = (function () {
  'use strict';

  /* ── Taxonomy ───────────────────────────────────────────────────────────
     Tier one is the LOCUS — four of these five are ours to fix; `life` is
     recorded and then left alone, because chasing it is how you earn a
     chargeback. Tier two is written per product family, so a soap customer is
     never asked whether the blade tugs. */
  var LOCI = [
    { key: 'schedule', label: 'The delivery schedule', sub: 'Too often, too much, or not often enough' },
    { key: 'product',  label: 'The product itself',    sub: 'It didn’t work for me' },
    { key: 'price',    label: 'The price',             sub: 'It costs more than I want to spend' },
    { key: 'service',  label: 'Something we got wrong', sub: 'An order, a charge, or support' },
    { key: 'life',     label: 'Nothing to do with you', sub: 'My circumstances changed' }
  ];

  var CAUSES = {
    schedule: { all: [
      { key: 'too_often',        label: 'It arrives faster than I use it' },
      { key: 'too_much_qty',     label: 'There’s too much in each box' },
      { key: 'not_often_enough', label: 'I run out before the next one comes' },
      { key: 'dont_want_sub',    label: 'I don’t want a subscription at all' }
    ] },
    product: {
      blades: [
        { key: 'tug',        label: 'It tugs or pulls' },
        { key: 'dull',       label: 'Blades go dull too quickly' },
        { key: 'irritation', label: 'Irritation or razor burn' },
        { key: 'handle',     label: 'The handle broke or feels cheap' }
      ],
      body: [
        { key: 'scent',       label: 'The scent isn’t for me' },
        { key: 'skin',        label: 'It didn’t agree with my skin' },
        { key: 'performance', label: 'It didn’t work as well as I hoped' },
        { key: 'texture',     label: 'The texture or consistency' }
      ]
    },
    price: { all: [
      { key: 'too_expensive',   label: 'It costs more than I want to spend' },
      { key: 'switching_brand', label: 'I’m switching to a cheaper brand', followup: 'competitor' },
      { key: 'cutting_back',    label: 'I’m cutting back generally' }
    ] },
    service: { all: [
      { key: 'late',    label: 'An order was late or never arrived' },
      { key: 'damaged', label: 'It arrived damaged' },
      { key: 'support', label: 'I contacted support and it wasn’t resolved' },
      { key: 'billing', label: 'I was charged when I shouldn’t have been' },
      { key: 'account', label: 'The account or website was hard to use' }
    ] },
    life: { all: [
      { key: 'no_need',    label: 'I don’t need it any more' },
      { key: 'stockpiled', label: 'I have plenty for now' },
      { key: 'other_life', label: 'Something else changed' }
    ] }
  };

  var CAUSE_PROMPT = {
    schedule: 'What’s wrong with the schedule?',
    product: 'What went wrong with it?',
    price: 'Tell us about the price.',
    service: 'What did we get wrong?',
    life: 'Fair enough — anything you’d tell us?'
  };
  var NOTE_PLACEHOLDER = {
    schedule: 'How often would have been right? (optional)',
    product: 'What happened? The more specific, the more likely we fix it. (optional)',
    price: 'What would have felt fair? (optional)',
    service: 'What happened, and when? (optional)',
    life: 'Anything else? (optional)'
  };

  /* Ranked by what customers already tell us unprompted in the CSAT archive:
     Harry's 323, Gillette 249, safety razor 67, Dollar Shave Club 62, Schick 29.
     158 responses name a rival AND discuss price; only 5 in 8,923 ever give a
     figure — which is the whole case for asking here, where it costs one tap. */
  var COMPETITORS = ['Harry’s', 'Gillette', 'A safety razor', 'Dollar Shave Club', 'Schick', 'A store brand', 'Something else'];

  var RX = {
    schedule: ['less_often', 'one_time_option', 'nothing'],
    product:  ['better_product', 'fixed_issue', 'different_product', 'nothing'],
    price:    ['lower_price', 'less_often', 'one_time_option', 'nothing'],
    service:  ['fixed_issue', 'faster_support', 'lower_price', 'nothing'],
    life:     ['nothing', 'less_often', 'one_time_option']
  };
  var RX_LABEL = {
    less_often: 'Less frequent delivery',
    one_time_option: 'Buying it without a subscription',
    better_product: 'A better version of the product',
    different_product: 'A different product that suited me',
    fixed_issue: 'Just fixing what went wrong',
    faster_support: 'Support that actually answered',
    lower_price: 'A lower price',
    nothing: 'Honestly, nothing — I was always going to go'
  };

  /* Every offer maps to something jr-account can already perform. An offer with
     no action behind it is a promise we cannot keep, which is exactly how
     "Support has your message" sat on screen for months doing nothing. */
  var OFFERS = {
    stretch_cadence: {
      locus: 'schedule', eyebrow: 'Easier than cancelling', h: 'Then let’s slow it down.',
      body: 'You don’t have to cancel to stop the boxes piling up. Stretch the gap so a shipment turns up when you actually need one.',
      actions: [{ label: 'Ship every 6 months', offer: 'stretch_to_6_months' }, { label: 'Skip the next one', offer: 'skip_1_month' }]
    },
    one_time: {
      locus: 'schedule', eyebrow: 'No subscription', h: 'Then buy them when you need them.',
      body: 'Fair enough — a subscription isn’t for everyone. The same pack is on the shop as a one-off, with no schedule and no auto-charge.',
      actions: [{ label: 'Show me the one-off pack', offer: 'convert_to_one_time' }]
    },
    fix_it: {
      locus: 'product', eyebrow: 'That’s on us', h: 'Let us fix it before you go.',
      body: 'A product that didn’t work isn’t something you should have to cancel over. Tell us what happened and we’ll make it right.',
      actions: [{ label: 'Send this to support', offer: 'support_handoff' }],
      needsMessage: true
    },
    price_pause: {
      locus: 'price', eyebrow: 'Fewer charges', h: 'You can be charged half as often.',
      body: 'Stretch to every 6 months and you’re billed twice a year instead of four times — same product, same price per box.',
      actions: [{ label: 'Cut my charges in half', offer: 'stretch_to_6_months' }, { label: 'Skip the next one', offer: 'skip_1_month' }]
    },
    make_good: {
      locus: 'service', eyebrow: 'We got this wrong', h: 'Let us put it right.',
      body: 'This one is ours, not yours. Tell us what happened and a real person will pick it up — your subscription stays exactly as it is meanwhile.',
      actions: [{ label: 'Put me through to support', offer: 'support_handoff' }],
      needsMessage: true
    },
    pause_life: {
      locus: 'life', eyebrow: 'No argument', h: 'Then park it rather than close it.',
      body: 'Push the next shipment well out instead. Nothing arrives and nothing is billed until then, and your pricing is held.',
      actions: [{ label: 'Push it out 6 months', offer: 'push_6_months' }]
    }
  };

  function esc(s) { var d = document.createElement('div'); d.textContent = (s == null ? '' : String(s)); return d.innerHTML; }
  function fmtDate(s) {
    if (!s) return '';
    try { return new Date(String(s).slice(0, 10) + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); }
    catch (e) { return String(s); }
  }
  function addMonths(iso, n) {
    var d = new Date((String(iso || '').slice(0, 10) || new Date().toISOString().slice(0, 10)) + 'T00:00:00');
    var day = d.getDate();
    d.setMonth(d.getMonth() + n);
    if (d.getDate() < day) d.setDate(0);
    return d.toISOString().slice(0, 10);
  }
  // Blades vs body & grooming, by WHAT SHIPS rather than what the SKU is called:
  // three-quarters of what looks like "hardware" is conversion bundles, i.e.
  // blade subscribers wearing a migration label who would be baffled to be
  // asked about build quality.
  function familyOf(title) {
    return /blade|refill|precision|sprint|razor|handle|conversion|upgrade|kit/i.test(String(title || '')) ? 'blades' : 'body';
  }

  // ⚠️ ONE OVERLAY AT A TIME. `open()` appends to <body>, and there are two
  // legitimate ways to call it twice: a double-click on "Cancel subscription",
  // and the two hosts both being mounted on one page (which jr-account.js
  // explicitly supports). Stacked overlays leave a dead scrim behind, and the
  // first one to close restores <html> scrolling while another is still open.
  var live = null;

  function open(o) {
    if (live) { try { live.close(); } catch (e) {} live = null; }
    o = o || {};
    var sub = o.sub || {};
    var call = o.call || function () { return Promise.reject(new Error('no transport')); };
    var family = familyOf(sub.title);
    var session = 'cx_' + Math.random().toString(36).slice(2, 10);
    var seq = 0;
    var S = { locus: null, cause: null, offerShown: null, rx: null, competitor: null, verbatim: '', hypothesis: null, confirmed: null, outcome: 'abandoned' };

    /* Telemetry is FIRE AND FORGET. A failed analytics write must never be
       visible to someone trying to cancel, and must never block them. Events
       are buffered and flushed on a timer AND on pagehide via sendBeacon —
       which is the only way an abandoned flow is ever recorded at all. */
    var buf = [];
    var flushTimer = null;
    function track(name, props) {
      // The cancellation split test (2026-09-17): every event from this overlay
      // is the 'flow' arm, whichever surface opened it.
      var p = props || {}; p.variant = 'flow'; p.surface = o.surface || 'account';
      buf.push({ name: name, seq: seq++, props: p });
      if (flushTimer) clearTimeout(flushTimer);
      flushTimer = setTimeout(flush, 900);
    }
    function flush(useBeacon) {
      if (!buf.length) return;
      var batch = buf.splice(0, 20);
      var payload = JSON.stringify({ action: 'track', session: session, events: batch });
      if (useBeacon && navigator.sendBeacon) {
        try { navigator.sendBeacon('/apps/jr/', new Blob([payload], { type: 'application/json' })); return; } catch (e) { /* fall through */ }
      }
      try {
        fetch('/apps/jr/', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin', body: payload, keepalive: true
        }).catch(function () {});
      } catch (e) { /* never surfaced */ }
    }
    function onHide() { track('cancel_flow_abandoned', { at_step: current }); flush(true); }
    window.addEventListener('pagehide', onHide);

    /* ── shell ─────────────────────────────────────────────────────────── */
    var wrap = document.createElement('div');
    // `jr-account` carries the design tokens onto a body-appended element.
    wrap.className = 'jr-account jr-cx-overlay';
    wrap.innerHTML =
      '<div class="jr-cx" role="dialog" aria-modal="true" aria-label="Cancel your subscription">'
      + '<button type="button" class="jr-cx__x" data-cx="close" aria-label="Close">&times;</button>'
      + '<div class="jr-cx__rail"' + (o.portrait ? ' style="background-image:url(' + esc(o.portrait) + ')"' : '') + '></div>'
      + '<div class="jr-cx__pane" data-cx-pane></div>'
      + '</div>';
    document.body.appendChild(wrap);
    document.documentElement.style.overflow = 'hidden';

    var pane = wrap.querySelector('[data-cx-pane]');
    var modal = wrap.querySelector('.jr-cx');
    var current = 'jeremy_message';

    function close() {
      live = null;
      window.removeEventListener('pagehide', onHide);
      document.removeEventListener('keydown', onKey);
      flush(true);
      document.documentElement.style.overflow = '';
      if (wrap.parentNode) wrap.parentNode.removeChild(wrap);
    }
    function onKey(e) { if (e.key === 'Escape') { track('cancel_flow_abandoned', { at_step: current, via: 'escape' }); close(); } }
    document.addEventListener('keydown', onKey);

    function render(step, html, opts) {
      current = step;
      opts = opts || {};
      // Jeremy is on screen for the opening message and for the thank-you, and
      // nowhere between: holding his gaze while someone edits a shipping
      // schedule reads as pressure rather than warmth.
      modal.classList.toggle('is-plain', !opts.portrait);
      pane.innerHTML = html;
      var h = pane.querySelector('h2');
      // preventScroll matters: focusing inside an overflow-clipped drawer is
      // exactly what blanked the account panel once before.
      if (h) { h.setAttribute('tabindex', '-1'); try { h.focus({ preventScroll: true }); } catch (e) {} }
      pane.scrollTop = 0;
    }
    function exitLink(label, go) {
      return '<div class="jr-cx__exit"><button type="button" class="jr-linkbtn" data-cx="' + go + '">' + esc(label) + '</button></div>';
    }
    function busy(btn, on, label) {
      if (!btn) return;
      btn.disabled = !!on;
      if (on) { btn.dataset.cxLabel = btn.textContent; btn.textContent = label || 'Working…'; }
      else if (btn.dataset.cxLabel) { btn.textContent = btn.dataset.cxLabel; }
    }

    /* ── 1 · the letter ────────────────────────────────────────────────── */
    function stepMessage() {
      render('jeremy_message',
        '<p class="jr-cx__eyebrow">Why Jeremy’s Razors exists</p>'
        + '<h2>Fight for Freedom<br>Build the Future</h2>'
        + '<p>I started Jeremy’s Razors to fight back. Harry’s pulled their ads over what we believe, so I built a razor company that would never do that to its own customers. We made a real impact, and we proved a company can stand its ground and still make something worth buying.</p>'
        + '<p>Now we’re building the future: a place where men and women can buy great products from a company that doesn’t hate them.</p>'
        + '<p>I understand you’re here to cancel, but I hope you’ll reconsider.</p>'
        + '<p class="jr-cx__sigline">Jeremy Boreing<span class="jr-cx__sigrole">Founder and CEO</span></p>'
        + '<div class="jr-cx__actions">'
        + '<button type="button" class="jr-act jr-act--primary" data-cx="keep">Keep my subscription</button>'
        + '<button type="button" class="jr-act" data-cx="diagnose">Continue</button>'
        + '</div>',
        { portrait: true });
      track('cancel_flow_step', { step: 'jeremy_message' });
    }

    /* ── 2 · the diagnosis ─────────────────────────────────────────────── */
    var DX = {
      unresolved_ticket: { h: 'We never got back to you, did we?', p: 'You wrote to us and the thread was closed without an answer. If that’s why you’re here, we’d like to put it right first.' },
      payment_failed:    { h: 'Is this about the payment that failed?', p: 'If the card just needs updating, that’s a two-minute fix and nothing about your subscription has to change.' },
      cadence_too_often: { h: 'Is it turning up too often?', p: 'If the boxes come faster than you shave, the schedule is the thing to change — not the subscription.' },
      cadence_vs_consumption: { h: 'We think we’re shipping faster than you use it.', p: 'That’s our mistake to fix, and it takes one tap — not a cancellation.' }
    };
    function stepDiagnose() {
      render('diagnosis', '<p class="jr-cx__eyebrow">Before you go</p><h2>One moment…</h2><p class="jr-muted">Checking your account.</p>');
      call({ action: 'cancel_context', subscription_id: sub.id }).then(function (res) {
        var hyp = res && res.hypothesis;
        var copy = hyp && DX[hyp];
        if (!copy) { stepLocus(); return; }
        S.hypothesis = hyp;
        track('cancel_flow_hypothesis_shown', { hypothesis: hyp, observed: res.observed || null });
        render('diagnosis',
          '<p class="jr-cx__eyebrow">Before you go</p>'
          + '<h2>' + esc(copy.h) + '</h2>'
          + '<p>' + esc(copy.p) + '</p>'
          + (res.basis ? '<p class="jr-fact">' + esc(res.basis) + '</p>' : '')
          + '<div class="jr-cx__actions">'
          + '<button type="button" class="jr-act jr-act--primary" data-cx="dx-yes">Yes, that’s it</button>'
          + '<button type="button" class="jr-act" data-cx="dx-no">No, something else</button>'
          + '</div>'
          + exitLink('Just cancel my subscription', 'to-prescription'));
      }).catch(function () { stepLocus(); });   // a missing guess is a degraded flow, not a broken one
    }

    /* ── 3 · the schedule fix, on the account’s own card ───────────── */
    function subCard() {
      return '<div class="jr-sub jr-cx__card">'
        + '<div class="jr-sub__row">'
        + (sub.imageUrl ? '<div class="jr-sub__media"><img src="' + esc(sub.imageUrl) + '" alt=""></div>' : '')
        + '<div class="jr-sub__body">'
        + '<div class="jr-sub__title">' + esc(sub.title || 'Your subscription')
        + (sub.variantTitle ? ' <span class="jr-muted">' + esc(sub.variantTitle) + '</span>' : '') + '</div>'
        + '<div class="jr-sub__meta">'
        + (sub.interval ? '<span>Every ' + esc(sub.interval) + '</span>' : '')
        + (sub.priceText ? '<span>' + esc(sub.priceText) + '</span>' : '')
        + '</div>'
        + (sub.nextAt ? '<div class="jr-sub__next">Next shipment: <strong>' + esc(fmtDate(sub.nextAt)) + '</strong></div>' : '')
        + '<div class="jr-sub__actions">'
        + '<button type="button" class="jr-act" data-cx="do" data-offer="skip_1_month">Skip a month</button>'
        + '<button type="button" class="jr-act" data-cx="do" data-offer="stretch_to_6_months">Ship every 6 months</button>'
        + '</div></div></div></div>';
    }
    function stepSchedule() {
      render('confirm_intent',
        '<p class="jr-cx__eyebrow">Before you go</p>'
        + '<h2>Change how often it ships</h2>'
        + '<p>If the boxes arrive faster than you shave, change the schedule instead of cancelling. Push the next one back, or stretch the gap so a shipment turns up when you actually need one.</p>'
        + subCard()
        + exitLink('No thanks — cancel my subscription', 'to-locus'));
      track('cancel_flow_step', { step: 'confirm_intent' });
    }

    /* ── 4 · locus (one tap, no Continue) ──────────────────────────────── */
    function stepLocus() {
      var rows = LOCI.map(function (l) {
        return '<li><label class="reason"><input type="radio" name="cx-locus" value="' + l.key + '">'
          + '<span class="reason__body"><span class="reason__t">' + esc(l.label) + '</span>'
          + '<span class="reason__sub">' + esc(l.sub) + '</span></span></label></li>';
      }).join('');
      render('locus',
        '<p class="jr-cx__eyebrow">One question</p>'
        + '<h2>We’re sorry to see you go.</h2>'
        + '<p>Please help us improve our product and our experience by telling us why you’re leaving. Which of these is closest?</p>'
        + '<ul class="reasons">' + rows + '</ul>'
        + exitLink('Skip this and cancel my subscription', 'to-prescription'));
      track('cancel_flow_step', { step: 'locus' });
    }

    /* ── 5 · cause ─────────────────────────────────────────────────────── */
    function causesFor(locus) { var set = CAUSES[locus]; return set.all || set[family] || set.blades; }
    function stepCause() {
      var rows = causesFor(S.locus).map(function (c) {
        return '<li><label class="reason"><input type="radio" name="cx-cause" value="' + c.key + '"'
          + (c.followup ? ' data-followup="' + c.followup + '"' : '') + '>'
          + '<span class="reason__body"><span class="reason__t">' + esc(c.label) + '</span></span></label></li>';
      }).join('');
      var locus = LOCI.filter(function (l) { return l.key === S.locus; })[0];
      render('cause',
        '<p class="jr-cx__eyebrow">' + esc(locus ? locus.label : '') + '</p>'
        + '<h2>' + esc(CAUSE_PROMPT[S.locus]) + '</h2>'
        + '<ul class="reasons">' + rows + '</ul>'
        + '<div data-cx-followup hidden></div>'
        + '<textarea class="jr-ta" data-cx-note placeholder="' + esc(NOTE_PLACEHOLDER[S.locus]) + '"></textarea>'
        + '<div class="jr-cx__actions"><button type="button" class="jr-act jr-act--primary" data-cx="cause-next" disabled>Continue</button></div>'
        + exitLink('Skip this and cancel my subscription', 'to-prescription'));
      track('cancel_flow_step', { step: 'cause' });
    }
    function renderCompetitor(host) {
      host.innerHTML = '<p class="jr-fu__q">Which one? It helps us more than you might think.</p>'
        + '<div class="jr-chips">' + COMPETITORS.map(function (c, i) {
            return '<button type="button" class="jr-chip" data-comp="' + i + '">' + esc(c) + '</button>';
          }).join('') + '</div>';
      host.hidden = false;
    }

    /* ── 6 · offer ─────────────────────────────────────────────────────── */
    function eligibleOffers() {
      var keys = Object.keys(OFFERS).filter(function (k) { return OFFERS[k].locus === S.locus; });
      // The one-time door belongs ONLY to the people telling us they don't want
      // a subscription. Offering it to anyone else is a nudge toward the exit.
      if (S.locus === 'schedule') keys = (S.cause === 'dont_want_sub') ? ['one_time'] : keys.filter(function (k) { return k !== 'one_time'; });
      return keys;
    }
    function stepOffer() {
      var eligible = eligibleOffers();
      if (!eligible.length) { stepPrescription(); return; }
      // Drawn at random from the eligible set so acceptance rates stay
      // comparable across offers rather than an artefact of what we chose to
      // show. The holdout that makes deflection measurable is assigned
      // server-side and is NOT wired yet — see the flow's step 6.
      var key = eligible[Math.floor(Math.random() * eligible.length)];
      var offer = OFFERS[key];
      S.offerShown = key;
      render('offer',
        '<p class="jr-cx__eyebrow">' + esc(offer.eyebrow) + '</p>'
        + '<h2>' + esc(offer.h) + '</h2>'
        + '<p>' + esc(offer.body) + '</p>'
        + (offer.needsMessage ? '<textarea class="jr-ta" data-cx-msg placeholder="What happened?"></textarea>' : '')
        + '<div class="jr-cx__actions">' + offer.actions.map(function (a, i) {
            return '<button type="button" class="jr-act' + (i === 0 ? ' jr-act--primary' : '') + '" data-cx="do" data-offer="' + a.offer + '">' + esc(a.label) + '</button>';
          }).join('') + '</div>'
        + exitLink('No thanks — cancel my subscription', 'to-prescription'));
      track('cancel_flow_offer_shown', { offer: key, eligible: eligible, assigned: 'random' });
    }

    /* ── 7 · prescription — the highest-value field in the flow ─────────── */
    function stepPrescription() {
      var keys = RX[S.locus] || RX.life;
      var rows = keys.map(function (k) {
        return '<li><label class="reason"><input type="radio" name="cx-rx" value="' + k + '">'
          + '<span class="reason__body"><span class="reason__t">' + esc(RX_LABEL[k]) + '</span></span></label></li>';
      }).join('');
      render('prescription',
        '<p class="jr-cx__eyebrow">Last thing</p>'
        + '<h2>What would have kept you?</h2>'
        + '<p>You’re cancelling either way — this one is only so we know what to fix.</p>'
        + '<ul class="reasons">' + rows + '</ul>'
        + '<div class="jr-cx__actions"><button type="button" class="jr-act jr-act--primary" data-cx="to-final">Continue</button></div>'
        + exitLink('Skip — just cancel', 'to-final'));
      track('cancel_flow_step', { step: 'prescription' });
    }

    /* ── 8 · confirm ───────────────────────────────────────────────────── */
    function stepFinal() {
      render('final',
        '<p class="jr-cx__eyebrow">Last step</p>'
        + '<h2>Cancel your subscription?</h2>'
        + '<p>Nothing else ships and you won’t be charged again. Whatever’s already on its way is still yours.</p>'
        + '<p>You can restart from your account any time — same product, one button.</p>'
        + '<div class="jr-cx__actions">'
        + '<button type="button" class="jr-act jr-act--danger" data-cx="cancel-confirm">Cancel my subscription</button>'
        + '<button type="button" class="jr-act jr-act--primary" data-cx="keep">Never mind, keep it</button>'
        + '</div>');
      track('cancel_flow_step', { step: 'final' });
    }

    /* ── terminals ─────────────────────────────────────────────────────── */
    function stepKept(lede) {
      S.outcome = 'kept';
      render('kept',
        '<p class="jr-cx__eyebrow">Thank you</p>'
        + '<h2>' + esc(o.tenureHeadline || 'Thank you for staying.') + '</h2>'
        + '<p class="jr-cx__lede">' + esc(lede || 'Your subscription is exactly as it was.') + '</p>'
        + (o.tenureLine ? '<p class="jr-cx__note">' + esc(o.tenureLine) + '</p>' : '')
        + '<p class="jr-cx__sigline">Jeremy Boreing<span class="jr-cx__sigrole">Founder and CEO</span></p>'
        + '<div class="jr-cx__actions"><button type="button" class="jr-act jr-act--primary" data-cx="close">Done</button></div>',
        { portrait: true });
    }
    function stepCancelled() {
      S.outcome = 'cancelled';
      render('cancelled',
        '<p class="jr-cx__eyebrow">Cancelled</p>'
        + '<h2>Done. Nothing else will ship.</h2>'
        + '<p>Sorry to see you go. Your account keeps a Restart button, so coming back is one click.</p>'
        + '<p>And if there’s something we got wrong, <strong>support@jeremysrazors.com</strong> reaches a real person.</p>'
        + '<div class="jr-cx__actions"><button type="button" class="jr-act" data-cx="close">Close</button></div>');
    }

    /* ── the one action performer ──────────────────────────────────────── */
    function perform(offer, btn) {
      var msgEl = pane.querySelector('[data-cx-msg]');
      var p, keptLede;
      if (offer === 'skip_1_month') {
        p = call({ action: 'skip', subscription_id: sub.id });
        keptLede = 'Pushed out a month. Nothing else changes.';
      } else if (offer === 'stretch_to_6_months') {
        p = call({ action: 'set_frequency', subscription_id: sub.id, unit: 'month', frequency: 6 });
        keptLede = 'Now shipping every 6 months — two charges a year instead of four.';
      } else if (offer === 'push_6_months') {
        p = call({ action: 'reschedule', subscription_id: sub.id, date: addMonths(sub.nextAt, 6) });
        keptLede = 'Parked. Nothing arrives and nothing is billed until then.';
      } else if (offer === 'support_handoff') {
        var text = (msgEl && msgEl.value || '').trim();
        if (!text) { if (msgEl) msgEl.focus(); return; }
        p = call({ action: 'support_request', subscription_id: sub.id, message: text, topic: S.cause || S.locus || 'cancel_flow' });
        keptLede = 'Support has your message, and your subscription is untouched.';
      } else if (offer === 'convert_to_one_time') {
        // Cancels the subscription, then hands them a buying path. A kept
        // CUSTOMER, not a kept subscriber — tracked as its own outcome so it
        // never inflates the deflection rate.
        p = call({ action: 'cancel', subscription_id: sub.id, reason: 'Converted to one-time' });
      } else { return; }

      busy(btn, true);
      p.then(function (res) {
        track('cancel_flow_offer_accepted', { offer: offer, from: S.offerShown, reason: S.cause || null });
        if (offer === 'convert_to_one_time') {
          track('cancel_flow_outcome', { outcome: 'converted_one_time', locus: S.locus, cause: S.cause });
          if (o.onCancelled) o.onCancelled();
          flush(true);
          window.location.href = o.oneTimeUrl || '/collections/all';
          return;
        }
        track('cancel_flow_outcome', { outcome: 'kept', via: 'offer', offer: offer, locus: S.locus, cause: S.cause });
        // Hand the server's own answer back, so the host applies the SAME
        // optimistic update as every other action rather than guessing a date.
        if (o.onKept) o.onKept(offer, res || {});
        stepKept(keptLede);
      }).catch(function (err) {
        busy(btn, false);
        var n = document.createElement('p');
        n.className = 'jr-fact is-bad';
        n.textContent = (err && err.message === 'no transport')
          ? 'We couldn’t reach the server. Please try again.'
          : 'We couldn’t do that just now — email support@jeremysrazors.com and we’ll sort it.';
        pane.appendChild(n);
      });
    }

    /* ── events ────────────────────────────────────────────────────────── */
    pane.addEventListener('change', function (e) {
      var t = e.target;
      if (t.name === 'cx-locus') {
        S.locus = t.value;
        track('cancel_flow_locus', { locus: S.locus });
        stepCause();
      } else if (t.name === 'cx-cause') {
        S.cause = t.value;
        track('cancel_flow_cause', { locus: S.locus, cause: S.cause });
        var next = pane.querySelector('[data-cx="cause-next"]');
        if (next) next.disabled = false;
        var fu = pane.querySelector('[data-cx-followup]');
        if (fu) {
          if (t.dataset.followup === 'competitor') renderCompetitor(fu);
          else { fu.hidden = true; fu.innerHTML = ''; }
        }
      } else if (t.name === 'cx-rx') {
        S.rx = t.value;
        track('cancel_flow_prescription', { would_have_kept: S.rx });
      }
    });

    wrap.addEventListener('click', function (e) {
      var chip = e.target.closest('[data-comp]');
      if (chip) {
        pane.querySelectorAll('[data-comp]').forEach(function (b) { b.classList.remove('is-on'); });
        chip.classList.add('is-on');
        S.competitor = COMPETITORS[+chip.dataset.comp];
        track('cancel_flow_competitor', { competitor: S.competitor });
        return;
      }
      var btn = e.target.closest('[data-cx]');
      if (!btn) {
        // Clicking the scrim closes, same as Escape. The door stays open.
        if (e.target === wrap) { track('cancel_flow_abandoned', { at_step: current, via: 'scrim' }); close(); }
        return;
      }
      var a = btn.dataset.cx;
      if (a === 'close') { if (S.outcome === 'abandoned') track('cancel_flow_abandoned', { at_step: current }); close(); return; }
      if (a === 'keep') {
        S.outcome = 'kept';
        track('cancel_flow_outcome', { outcome: 'kept', via: current, locus: S.locus, cause: S.cause });
        stepKept('Your subscription is exactly as it was.');
        return;
      }
      if (a === 'diagnose') { stepDiagnose(); return; }
      if (a === 'dx-yes') {
        S.confirmed = true;
        track('cancel_flow_hypothesis_answered', { hypothesis: S.hypothesis, confirmed: true });
        if (S.hypothesis === 'cadence_too_often' || S.hypothesis === 'cadence_vs_consumption') { S.locus = 'schedule'; S.cause = 'too_often'; stepSchedule(); }
        else if (S.hypothesis === 'payment_failed') { S.locus = 'service'; S.cause = 'billing'; stepOffer(); }
        else { S.locus = 'service'; S.cause = 'support'; stepOffer(); }
        return;
      }
      if (a === 'dx-no') {
        S.confirmed = false;
        track('cancel_flow_hypothesis_answered', { hypothesis: S.hypothesis, confirmed: false });
        stepLocus(); return;
      }
      if (a === 'to-locus') { track('cancel_flow_offer_declined', { via: 'schedule' }); stepLocus(); return; }
      if (a === 'cause-next') {
        var note = pane.querySelector('[data-cx-note]');
        S.verbatim = (note && note.value || '').slice(0, 2000);
        stepOffer(); return;
      }
      if (a === 'to-prescription') {
        if (S.offerShown) track('cancel_flow_offer_declined', { offer: S.offerShown });
        stepPrescription(); return;
      }
      if (a === 'to-final') { stepFinal(); return; }
      if (a === 'do') { perform(btn.dataset.offer, btn); return; }
      if (a === 'cancel-confirm') {
        busy(btn, true, 'Cancelling…');
        call({ action: 'cancel', subscription_id: sub.id, reason: S.cause || S.locus || 'No reason given' })
          .then(function () {
            track('cancel_flow_outcome', {
              outcome: 'cancelled', locus: S.locus, cause: S.cause,
              verbatim: S.verbatim || null, would_have_kept: S.rx || null,
              competitor: S.competitor || null,
              hypothesis: S.hypothesis, hypothesis_confirmed: S.confirmed,
              offer_shown: S.offerShown
            });
            if (o.onCancelled) o.onCancelled();
            stepCancelled();
          })
          .catch(function () {
            busy(btn, false);
            var n = document.createElement('p');
            n.className = 'jr-fact is-bad';
            n.textContent = 'We couldn’t cancel that just now. Please email support@jeremysrazors.com and we will do it for you.';
            pane.appendChild(n);
          });
      }
    });

    track('cancel_flow_opened', {
      subscription_id: sub.id, product: sub.title || null, family: family,
      is_holdout: false      // assigned server-side in a later step; never client-side
    });
    stepMessage();
    live = { close: close };
    return live;
  }

  return { open: open };
})();
